"use client";

import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useReactiveSearchParams } from "@/lib/Hooks/useReactiveSearchParams";
import styled from "styled-components";
import IconFlightChange from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconFlightChange.js";
import MultiSelect from "@/components/UI/MultiSelect/MultiSelect";
import SearchableCheckboxList from "@/components/UI/SearchableCheckboxList/SearchableCheckboxList";
import useAirlines from "@/app/api/useAirlines";
import { useFilterState } from "@/lib/Providers/FilterStateProvider/FilterStateProvider";
import { AppConfig } from "@/lib/Types/AppConfig";

const FilterWrapper = styled.div<{ $expanded?: boolean }>(({ $expanded }) => ({
  position: "relative",
  width: "100%",
  marginTop: $expanded ? 0 : "1rem",
}));

const NewBadge = styled.span(({ theme }) => ({
  backgroundColor:
    theme.kallisto.color.background.interactive.backgroundPrimaryDefault,
  position: "absolute",
  top: "-8px",
  right: "8px",
  color: theme.kallisto.color.text.static.textBrandContrastPrimary,
  fontSize: "10px",
  fontWeight: 700,
  textTransform: "uppercase",
  padding: "2px 6px",
  borderRadius: "4px",
  letterSpacing: "0.5px",
  display: "inline-block",
  zIndex: 1,
}));

interface AirlineFilterProps {
  delayedUpdate?: boolean;
  expanded?: boolean;
  appConfig: AppConfig;
}

const AirlineFilter: React.FC<AirlineFilterProps> = ({
  delayedUpdate = false,
  expanded = false,
  appConfig,
}) => {
  const searchParams = useReactiveSearchParams();
  const { tempFilters, updateFilter, updateTempFilterArray } = useFilterState();

  const { data, isLoading } = useAirlines(appConfig);

  const [selectedAirlines, setSelectedAirlines] = useState<string[]>(() => {
    return delayedUpdate
      ? tempFilters.get("airline")?.split(",") ?? []
      : searchParams.get("airline")?.split(",") ?? [];
  });

  useEffect(() => {
    setSelectedAirlines(searchParams.get("airline")?.split(",") ?? []);
  }, [searchParams]);

  const handleAirlineChange = useCallback(
    (newSelectedAirlines: string[]) => {
      setSelectedAirlines(newSelectedAirlines);

      if (delayedUpdate) {
        updateTempFilterArray("airline", newSelectedAirlines);
      } else {
        updateFilter(
          "airline",
          newSelectedAirlines.length > 0
            ? newSelectedAirlines.join(",")
            : null,
        );
      }
    },
    [updateFilter, updateTempFilterArray, delayedUpdate],
  );

  const options = useMemo(
    () =>
      data && data.length > 0
        ? data
          .map((airline) => ({
            value: airline.code,
            label: airline.name,
          }))
          .sort((a, b) => a.label.localeCompare(b.label))
        : [],
    [data],
  );

  return (
    <FilterWrapper data-testid="airlineFilter" $expanded={expanded}>
      {expanded ? (
        <SearchableCheckboxList
          options={options}
          value={selectedAirlines}
          onChange={handleAirlineChange}
          delayedUpdate={delayedUpdate}
          searchLabel="Airline suchen"
          listAriaLabel="Airlines"
          emptyStateText="Keine Airlines gefunden"
          formatResultCount={(filtered, total) =>
            `${filtered} von ${total} Airlines`
          }
          testIdPrefix="airline"
          loading={isLoading}
        />
      ) : (
        <MultiSelect
          options={options}
          value={selectedAirlines}
          onChange={handleAirlineChange}
          placeholder="Beliebig"
          label="Airline eingeben"
          icon={<IconFlightChange size={24} aria-hidden="true" />}
          aria-label="Wählen Sie eine oder mehrere Airlines"
          aria-describedby="airline-description"
          dataTestId="airlineList"
          loading={isLoading}
        />
      )}
      <span id="airline-description" className="sr-only">
        Wählen Sie eine oder mehrere Airlines aus der Liste aus. Verwenden Sie
        die Pfeiltasten, um durch die Optionen zu navigieren, die Leertaste zum
        Auswählen und Enter zum Bestätigen.
      </span>
      {!expanded && <NewBadge>Neu</NewBadge>}
    </FilterWrapper>
  );
};

export default AirlineFilter;
